import { Combobox } from "design-system/organisms/Combobox"
import { cn } from "design-system/utils"
import { Plus } from "lucide-react"
import { useInstagramIntegrationsQuery } from "modules/settings/hooks/instagram/useInstagramIntegrationsQuery"
import { useDebouncedValue } from "modules/shared/hooks/useDebouncedValue"
import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { InstagramIntegrationsRespondeData } from "schema"
import { z } from "zod"

type Option =
  | (React.ComponentProps<typeof Combobox>["options"][number] & {
      data?: z.infer<typeof InstagramIntegrationsRespondeData>[number]
    })
  | undefined

type InstagramAccountFormSelectorProps = {
  onChange: (value: Option) => void
  placeholder: string
  emptyPlaceholder: string
  searchPlaceholder: string
  className?: string
  avoidDisplaySelected?: boolean
  hasCreateNew?: boolean
  value?: string | null
  isDisabled?: boolean
}

const InstagramAccountFormSelector = ({
  onChange,
  placeholder,
  emptyPlaceholder,
  searchPlaceholder,
  className,
  avoidDisplaySelected,
  hasCreateNew,
  value,
  isDisabled,
}: InstagramAccountFormSelectorProps) => {
  const { t } = useTranslation()
  const [searchQuery, setSearchQuery] = useState("")
  const debouncedSearchQuery = useDebouncedValue({
    value: searchQuery,
  })
  const [selectedValue, setSelectedValue] = useState<Option>(undefined)

  const { data, isPending } = useInstagramIntegrationsQuery()

  const options = useMemo(() => {
    if (!data) return []

    return data
      .filter(
        (e) =>
          e.name.match(new RegExp(debouncedSearchQuery, "gi")) ||
          e.username.match(new RegExp(debouncedSearchQuery, "gi")),
      )
      .map((account) => {
        return {
          label: `${account.name} - @${account.username}`,
          value: account.id,
          image: account.profilePicture ?? "/insta-no-profile.png",
          data: account,
        }
      })
  }, [data, debouncedSearchQuery])

  function onComboboxChange(option: Option) {
    if (!avoidDisplaySelected) {
      setSelectedValue(option)
    }
    onChange(option)
  }

  useEffect(() => {
    if (value && data) {
      const result = options.find((e) => e.value === value)

      if (result) {
        setSelectedValue(result)
      }
    }
  }, [value, data, options])

  return (
    <div className={cn("flex items-center space-x-4", className)}>
      <Combobox
        options={options}
        value={selectedValue}
        onChange={onComboboxChange}
        placeholder={placeholder}
        emptyPlaceholder={emptyPlaceholder}
        searchPlaceholder={searchPlaceholder}
        disabled={isPending || isDisabled}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        className="w-full"
        actions={
          hasCreateNew
            ? [
                {
                  icon: Plus,
                  text: t("campaigns.trigger.metadata.instagramDirectMessage.addAccount"),
                  to: "/settings/channels/instagram",
                  target: "_blank",
                },
              ]
            : undefined
        }
      />
    </div>
  )
}

export default InstagramAccountFormSelector
